import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ResidueType } from "../../types/residue.type";
import { CategoryType } from "../../types/category.type";
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import { api } from "../../api/api";
import { DownOutlined, FilePdfOutlined } from "@ant-design/icons";
import { Currency } from "../../types/currency.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { canEdit, hasAdminAccess } from "../../utils/roleUtils";
import { useAuth } from "../../contexts/auth.context";
import {useExchangeRate} from "../../contexts/exchangeRate.context";
import CostOverviewModal, {
  averageCostSourceColor,
  averageCostSourceLabel,
} from "../../components/CostOverview/costOverview.component";

const { Title } = Typography;
const { Search } = Input;

export default function Price() {
  const { authState } = useAuth();
  const { type } = useParams();
  const [data, setData] = useState<ResidueType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<ResidueType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isCharacteristicsModalOpen, setIsCharacteristicsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [characteristicsForm] = Form.useForm();
  const [characteristicsLoading, setCharacteristicsLoading] = useState<boolean>(false);
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [priceLoadingDetailed, setPriceLoadingDetailed] = useState(false);
  const [priceLoadingPretty, setPriceLoadingPretty] = useState(false);
  const [priceListPdfLoading, setPriceListPdfLoading] = useState(false);
  const {exchangeRate} = useExchangeRate();
  const [hasResidue, setHasResidue] = useState<boolean>(false);

  // Категории
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState<boolean>(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState<boolean>(false);
  const [assignLoading, setAssignLoading] = useState<boolean>(false);
  const [categoryActionLoading, setCategoryActionLoading] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>("");
  const [assignForm] = Form.useForm();

  // Умный прайс: редактор процентов прибыли категории продукции
  const [profitCategory, setProfitCategory] = useState<CategoryType | null>(null);
  const [profitForm] = Form.useForm();
  const [profitLoading, setProfitLoading] = useState<boolean>(false);

  // Умный прайс: редактор ручной себестоимости полуфабриката
  const [manualCostProduce, setManualCostProduce] = useState<ResidueType | null>(null);
  const [manualCostForm] = Form.useForm();
  const [manualCostLoading, setManualCostLoading] = useState<boolean>(false);

  // Обзор себестоимости: расшифровка расходников и партий
  const [costOverviewProduce, setCostOverviewProduce] = useState<ResidueType | null>(null);
  const [isCostOverviewOpen, setIsCostOverviewOpen] = useState<boolean>(false);

  const handleOpenCostOverview = (produce: ResidueType) => {
    setCostOverviewProduce(produce);
    setIsCostOverviewOpen(true);
  };



  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/residue", {
        params: {
          page,
          type,
          limit: pageSize,
          search: searchTerm,
          hasResidue: hasResidue ? 'true' : undefined,
          category: categoryFilter || undefined,
        },
      });

      const { data, total } = response.data;

      setData(data);
      setTotal(total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение остатка.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("/category", { params: { type } });
      setCategories(response.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, searchTerm, type, hasResidue, categoryFilter]);

  useEffect(() => {
    // Сбрасываем фильтр категории при смене типа и подгружаем категории
    setCategoryFilter(undefined);
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const handleOpenModal = (item: ResidueType) => {
    setSelectedItem(item);
    form.setFieldsValue({
      amount: item.price[item.price.currency],
      currency: item.price.currency,
      exchangeRate: item.price.exchangeRate,
      lowTradePrice: item.lowTradePrice
        ? item.lowTradePrice[item.price.currency]
        : undefined,
      upTradePrice: item.upTradePrice
        ? item.upTradePrice[item.price.currency]
        : undefined,
      minDiscountPercent: undefined,
      upMarkupPercent: undefined,
    });
    setIsModalOpen(true);
  };

  // Авто-пересчёт цены мин/до от прайса (amount) при вводе процентов
  const handleEditValuesChange = (changed: any, all: any) => {
    const amount = all.amount;

    if ("minDiscountPercent" in changed || "amount" in changed) {
      const disc = all.minDiscountPercent;
      if (amount !== undefined && amount !== null && disc !== undefined && disc !== null) {
        form.setFieldsValue({
          lowTradePrice: roundToThreeDecimals(amount * (1 - disc / 100)),
        });
      }
    }

    if ("upMarkupPercent" in changed || "amount" in changed) {
      const markup = all.upMarkupPercent;
      if (amount !== undefined && amount !== null && markup !== undefined && markup !== null) {
        form.setFieldsValue({
          upTradePrice: roundToThreeDecimals(amount * (1 + markup / 100)),
        });
      }
    }
  };

  const handleOpenCharacteristicsModal = (item: ResidueType) => {
    setSelectedItem(item);
    characteristicsForm.setFieldsValue({
      characteristics: item.characteristics && item.characteristics.length > 0
        ? item.characteristics
        : [{ key: "", value: "" }],
    });
    setIsCharacteristicsModalOpen(true);
  };

  const handleOpenAssignModal = (item: ResidueType) => {
    setSelectedItem(item);
    assignForm.setFieldsValue({
      categoryId: item.category?._id ?? undefined,
    });
    setIsAssignModalOpen(true);
  };

  const handleAssignCategory = async (values: { categoryId?: string }) => {
    setAssignLoading(true);
    try {
      await api.post("/category/assign", {
        type,
        itemId: selectedItem?._id,
        categoryId: values.categoryId || null,
      });
      message.success("Категория позиции обновлена");
      setIsAssignModalOpen(false);
      setSelectedItem(null);
      assignForm.resetFields();
      fetchData();
    } catch (error: any) {
      console.error("Error assigning category:", error);
      message.error(error.response?.data?.error || "Ошибка при назначении категории");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      message.warning("Введите название категории");
      return;
    }
    setCategoryActionLoading(true);
    try {
      await api.post("/category", { name: newCategoryName.trim(), type });
      message.success("Категория создана");
      setNewCategoryName("");
      fetchCategories();
    } catch (error: any) {
      console.error("Error creating category:", error);
      message.error(error.response?.data?.error || "Ошибка при создании категории");
    } finally {
      setCategoryActionLoading(false);
    }
  };

  const handleRenameCategory = async (category: CategoryType) => {
    const name = window.prompt("Новое название категории", category.name);
    if (name === null) return;
    if (!name.trim()) {
      message.warning("Название не может быть пустым");
      return;
    }
    try {
      await api.put(`/category/${category._id}`, { name: name.trim() });
      message.success("Категория переименована");
      fetchCategories();
      fetchData();
    } catch (error: any) {
      console.error("Error renaming category:", error);
      message.error(error.response?.data?.error || "Ошибка при переименовании");
    }
  };

  const handleOpenProfit = (category: CategoryType) => {
    setProfitCategory(category);
    profitForm.setFieldsValue({
      minProfitPercent: category.minProfitPercent ?? undefined,
      maxDiscountPercent: category.maxDiscountPercent ?? undefined,
    });
  };

  const handleSaveProfit = async () => {
    if (!profitCategory) return;
    const values = await profitForm.validateFields();
    setProfitLoading(true);
    try {
      await api.put(`/category/${profitCategory._id}`, {
        name: profitCategory.name,
        minProfitPercent:
          values.minProfitPercent === undefined ? null : values.minProfitPercent,
        maxDiscountPercent:
          values.maxDiscountPercent === undefined ? null : values.maxDiscountPercent,
      });
      message.success("Проценты категории сохранены");
      setProfitCategory(null);
      fetchCategories();
    } catch (error: any) {
      console.error("Error saving profit percents:", error);
      message.error(error.response?.data?.error || "Ошибка при сохранении");
    } finally {
      setProfitLoading(false);
    }
  };

  const handleOpenManualCost = (item: ResidueType) => {
    setManualCostProduce(item);
    manualCostForm.setFieldsValue({
      amountUZS: item.manualCost?.UZS ?? undefined,
    });
  };

  const saveManualCost = async (amountUZS: number | null) => {
    if (!manualCostProduce) return;
    setManualCostLoading(true);
    try {
      await api.post(`/price/${manualCostProduce._id}/manual-cost`, { amountUZS });
      message.success(amountUZS === null ? "Ручная себестоимость сброшена" : "Ручная себестоимость сохранена");
      setManualCostProduce(null);
      fetchData();
    } catch (error: any) {
      console.error("Error saving manual cost:", error);
      message.error(error.response?.data?.error || "Ошибка при сохранении");
    } finally {
      setManualCostLoading(false);
    }
  };

  const handleSaveManualCost = async () => {
    const values = await manualCostForm.validateFields();
    await saveManualCost(values.amountUZS === undefined ? null : values.amountUZS);
  };

  const handleDeleteCategory = (category: CategoryType) => {
    Modal.confirm({
      title: `Удалить категорию «${category.name}»?`,
      content: "Категория будет снята со всех позиций. Цены не изменятся.",
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: async () => {
        try {
          await api.delete(`/category/${category._id}`);
          message.success("Категория удалена");
          if (categoryFilter === category._id) {
            setCategoryFilter(undefined);
          }
          fetchCategories();
          fetchData();
        } catch (error: any) {
          console.error("Error deleting category:", error);
          message.error(error.response?.data?.error || "Ошибка при удалении");
        }
      },
    });
  };

  const handleExport = () => {
    downloadFile("/export/residues", "residue_data.xlsx", { type });
  };

  const handleGetPriceDetailed = async () => {
    setPriceLoadingDetailed(true);
    try {
      await downloadFile("/price/list", `price_list_${type}.xlsx`, { 
        type,
        hasResidue: hasResidue ? 'true' : undefined,
      });
    } finally {
      setPriceLoadingDetailed(false);
    }
  };

  const handleGetPricePretty = async () => {
    setPriceLoadingPretty(true);
    try {
      await downloadFile("/price/list-pretty", `price_pretty_${type}.pdf`, {
        type,
        hasResidue: hasResidue ? 'true' : undefined,
      });
    } finally {
      setPriceLoadingPretty(false);
    }
  };

  const handleGetPriceListPdf = async () => {
    setPriceListPdfLoading(true);
    try {
      await downloadFile("/price/price-list-pdf", "price_list.pdf");
    } finally {
      setPriceListPdfLoading(false);
    }
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
      width: 200,
    },
    {
      title: "Остаток",
      dataIndex: "residue",
      key: "residue",
      width: 100,
      render: (_: any, record: ResidueType) => {
        return record.residue !== undefined
          ? `${roundToThreeDecimals(record.residue)}`
          : "Н/Д";
      },
    },
    {
      title: "Прайс",
      key: "price",
      width: 140,
      render: (_: any, record: ResidueType) => {
        if (!record.price || record.price.UZS === undefined) return "Н/Д";
        return (
          <Space direction="vertical" size={0}>
            <span style={{ fontWeight: 600 }}>{roundToThreeDecimals(record.price.UZS)} сум</span>
            <span style={{ color: "#888", fontSize: 12 }}>
              {roundToThreeDecimals(record.price.USD)} $
            </span>
          </Space>
        );
      },
    },
    {
      title: "Цена мин",
      key: "lowTradePrice",
      width: 140,
      render: (_: any, record: ResidueType) => {
        if (!record.lowTradePrice || record.lowTradePrice.UZS === undefined) return "Н/Д";
        return (
          <Space direction="vertical" size={0}>
            <span style={{ fontWeight: 600 }}>
              {roundToThreeDecimals(record.lowTradePrice.UZS)} сум
            </span>
            <span style={{ color: "#888", fontSize: 12 }}>
              {roundToThreeDecimals(record.lowTradePrice.USD)} $
            </span>
          </Space>
        );
      },
    },
    {
      // Средняя себестоимость по партиям — перенесена сюда из раздела «Продукция».
      // Для материалов тоже осмысленна: показывает, по какой цене лежит остаток.
      title: "Средняя себест. (партии)",
      key: "averageCost",
      width: 170,
      render: (_: any, record: ResidueType) => {
        if (!record.averageCost) return <span style={{ color: "#999" }}>Н/Д</span>;

        const source = record.averageCost.source;
        return (
          <Space direction="vertical" size={0}>
            <span style={{ fontWeight: 600 }}>
              {roundToThreeDecimals(record.averageCost.averageCostUZS)} сум
            </span>
            <span style={{ color: "#888", fontSize: 12 }}>
              {roundToThreeDecimals(record.averageCost.averageCostUSD)} $
            </span>
            {source && (
              <Tag
                color={averageCostSourceColor[source]}
                title={averageCostSourceLabel[source]}
              >
                {source === "batches"
                  ? `партии (${record.averageCost.batchCount ?? 0})`
                  : source === "lastBatch"
                  ? "последняя партия"
                  : source === "manual"
                  ? "ручная"
                  : source === "recipe"
                  ? "по расчёту"
                  : "нет данных"}
              </Tag>
            )}
          </Space>
        );
      },
    },
    ...(type === "produces"
      ? [
          {
            title: "Себест. (расчёт)",
            key: "recipeCost",
            width: 160,
            render: (_: any, record: ResidueType) => {
              if (!record.recipeCost) return <span style={{ color: "#999" }}>Н/Д</span>;
              return (
                <Space direction="vertical" size={0}>
                  <span style={{ fontWeight: 600 }}>
                    {roundToThreeDecimals(record.recipeCost.UZS)} сум
                  </span>
                  <span style={{ color: "#888", fontSize: 12 }}>
                    {roundToThreeDecimals(record.recipeCost.USD)} $
                  </span>
                  {record.manualCost && (record.manualCost.UZS || record.manualCost.USD) ? (
                    <Tag color="purple">ручная</Tag>
                  ) : null}
                  {record.recipeCost.incomplete && (
                    <Tag
                      color="warning"
                      title={`Нет цен: ${record.recipeCost.missingMaterials.slice(0, 5).join(", ")}`}
                    >
                      неполная
                    </Tag>
                  )}
                </Space>
              );
            },
          },
          {
            // Пол продажи: ниже этой цены КП и заказ-наряд позицию не пропустят.
            // Считает backend теми же правилами, что видит продажник в форме.
            title: "Макс. скидка (цена)",
            key: "maxDiscountPrice",
            width: 170,
            render: (_: any, record: ResidueType) => {
              if (!record.maxDiscountPrice) {
                return <span style={{ color: "#999" }}>Н/Д</span>;
              }
              const { UZS, USD, discountPercentUZS, discountPercentUSD } =
                record.maxDiscountPrice;
              return (
                <Tooltip
                  title={`Минимальная сумма, за которую продажник может продать и выставить КП: прайс минус максимальная скидка (потолок категории или скидка до мин. цены — что раньше упрётся). Скидка: ${discountPercentUZS}% в сумах, ${discountPercentUSD}% в долларах`}
                >
                  <Space direction="vertical" size={0}>
                    <span style={{ fontWeight: 600 }}>
                      {roundToThreeDecimals(UZS)} сум
                    </span>
                    <span style={{ color: "#888", fontSize: 12 }}>
                      {roundToThreeDecimals(USD)} $
                    </span>
                    <Tag color="orange">скидка до {discountPercentUZS}%</Tag>
                  </Space>
                </Tooltip>
              );
            },
          },
          {
            // Что остаётся на самой низкой допустимой цене: (цена / себест. − 1) × 100.
            title: "Прибыль (макс. скидка)",
            key: "maxDiscountProfit",
            width: 170,
            render: (_: any, record: ResidueType) => {
              const profitUZS = record.maxDiscountPrice?.profitPercentUZS;
              const profitUSD = record.maxDiscountPrice?.profitPercentUSD;

              if (profitUZS == null && profitUSD == null) {
                return (
                  <Tooltip title="Нет себестоимости — прибыль считать не от чего">
                    <span style={{ color: "#999" }}>Н/Д</span>
                  </Tooltip>
                );
              }

              // Минус — продажа по максимальной скидке уходит ниже себестоимости.
              const color = (value?: number | null) =>
                value == null ? "#999" : value < 0 ? "#cf1322" : "#389e0d";

              return (
                <Tooltip title="Прибыль к себестоимости на цене с максимальной скидкой: (макс. скидка цена / себестоимость − 1) × 100">
                  <Space direction="vertical" size={0}>
                    <span style={{ fontWeight: 600, color: color(profitUZS) }}>
                      {profitUZS == null ? "Н/Д" : `${profitUZS}%`} сум
                    </span>
                    <span style={{ fontSize: 12, color: color(profitUSD) }}>
                      {profitUSD == null ? "Н/Д" : `${profitUSD}%`} $
                    </span>
                  </Space>
                </Tooltip>
              );
            },
          },
          {
            title: "Проценты категории",
            key: "profitPercents",
            width: 160,
            render: (_: any, record: ResidueType) => {
              // Проценты задаются только у категории — показываем те, по которым
              // считаются цены этой позиции (пусто — работает дефолт настроек).
              const category = categories.find((c) => c._id === record.category?._id);
              const tags: React.ReactNode[] = [];
              if (category?.minProfitPercent != null) {
                tags.push(
                  <Tag key="min" color="cyan">
                    мин {category.minProfitPercent}%
                  </Tag>
                );
              }
              if (category?.maxDiscountPercent != null) {
                tags.push(
                  <Tag key="disc" color="purple">
                    скидка до {category.maxDiscountPercent}%
                  </Tag>
                );
              }
              if (tags.length === 0) {
                return (
                  <Tooltip title="У категории проценты не заданы — работает дефолт из настроек">
                    <span style={{ color: "#999" }}>из настроек</span>
                  </Tooltip>
                );
              }
              return <Space size={[0, 4]} wrap>{tags}</Space>;
            },
          },
        ]
      : []),
    {
      title: "Категория",
      key: "category",
      width: 150,
      render: (_: any, record: ResidueType) => {
        return record.category ? (
          <Tag color="geekblue">{record.category.name}</Tag>
        ) : (
          <span style={{ color: "#999" }}>Без категории</span>
        );
      },
    },
    {
      title: "Характеристики",
      key: "characteristics",
      width: 250,
      render: (_: any, record: ResidueType) => {
        if (!record.characteristics || record.characteristics.length === 0) {
          return <span style={{ color: "#999" }}>Нет характеристик</span>;
        }
        return (
          <Space wrap>
            {record.characteristics.map((char, index) => (
              <Tag key={index} color="blue">
                {char.key}: {char.value}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 150,
      fixed: "right" as "right",
      render: (_: any, record: ResidueType) => {
        const menu = (
          <Menu>
            {canEdit(authState.user?.role) && (
              <>
              <Menu.Item onClick={() => handleOpenModal(record)}>
                  Изменить цену
                </Menu.Item>
                <Menu.Item onClick={() => handleOpenCharacteristicsModal(record)}>
                  Изменить характеристики
              </Menu.Item>
              </>
            )}
            {hasAdminAccess(authState.user?.role) && (
              <Menu.Item onClick={() => handleOpenAssignModal(record)}>
                Изменить категорию
              </Menu.Item>
            )}
            {hasAdminAccess(authState.user?.role) && type === "produces" && (
              <Menu.Item onClick={() => handleOpenManualCost(record)}>
                Ручная себест.
              </Menu.Item>
            )}
            {type === "produces" && (
              <Menu.Item onClick={() => handleOpenCostOverview(record)}>
                Посмотреть обзор
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };


  const handleSubmitForm = async (values: {
    currency: Currency;
    amount: number;
    lowTradePrice?: number;
    upTradePrice?: number;
  }) => {
    setConfirmLoading(true);

    const data: any = {
      currency: values.currency,
      amount: values.amount,
      type,
      exchangeRate: exchangeRate
    };

    // Передаем lowTradePrice только если оно указано, иначе null для удаления
    if (values.lowTradePrice !== undefined && values.lowTradePrice !== null) {
      data.lowTradePrice = values.lowTradePrice;
    } else {
      data.lowTradePrice = null;
    }

    // Передаем upTradePrice только если оно указано, иначе null для удаления
    if (values.upTradePrice !== undefined && values.upTradePrice !== null) {
      data.upTradePrice = values.upTradePrice;
    } else {
      data.upTradePrice = null;
    }
    
    try {
      await api.post(`/price/${selectedItem?._id}`, data);

      message.success("Цены обновлены");
      form.resetFields();
      fetchData();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving price:", error);
      message.error(error.response.data.error);
    }
  };

  const handleSubmitCharacteristics = async (values: {
    characteristics: Array<{ key: string; value: string }>;
  }) => {
    setCharacteristicsLoading(true);

    // Фильтруем пустые характеристики
    const filteredCharacteristics = values.characteristics.filter(
      (char) => char.key.trim() !== "" && char.value.trim() !== ""
    );

    const data = {
      type,
      characteristics: filteredCharacteristics,
    };

    try {
      await api.post(`/price/${selectedItem?._id}/characteristics`, data);
      message.success("Характеристики обновлены");
      characteristicsForm.resetFields();
      fetchData();
      setIsCharacteristicsModalOpen(false);
      setCharacteristicsLoading(false);
    } catch (error: any) {
      setCharacteristicsLoading(false);
      console.error("Error saving characteristics:", error);
      message.error(error.response?.data?.error || "Ошибка при сохранении характеристик");
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title>Прайсы</Title>
      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
          style={{ width: 300 }}
        />

        <Space>
          <span>Имеет остаток:</span>
          <Switch
            checked={hasResidue}
            onChange={setHasResidue}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Space>

        <Select
          allowClear
          placeholder="Все категории"
          style={{ width: 220 }}
          value={categoryFilter}
          onChange={(value) => {
            setCategoryFilter(value);
            setPage(1);
          }}
          options={categories.map((c) => ({ label: c.name, value: c._id }))}
        />

        {hasAdminAccess(authState.user?.role) && (
          <Button onClick={() => setIsCategoriesModalOpen(true)}>Категории</Button>
        )}

        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="default"
          loading={priceLoadingPretty}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleGetPricePretty}
        >
          Прайс
        </Button>
        {type === "produces" && (
          <Button
            type="default"
            loading={priceListPdfLoading}
            icon={<FilePdfOutlined />}
            onClick={handleGetPriceListPdf}
          >
            Прайс лист PDF
          </Button>
        )}
        {hasAdminAccess(authState.user?.role) && (
          <Button
            type="default"
            loading={priceLoadingDetailed}
            icon={<PiMicrosoftExcelLogoFill />}
            onClick={handleGetPriceDetailed}
          >
            Прайс (подробный)
          </Button>
        )}
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total,
          pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Изменить прайс"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedItem(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form
          form={form}
          onFinish={handleSubmitForm}
          onValuesChange={handleEditValuesChange}
          layout="vertical"
        >
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select placeholder="Выберите валюту">
              {Object.values(Currency).map((currency) => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Прайс"
            rules={[{ required: true, message: "Введите прайс" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="minDiscountPercent"
            label="Скидка от прайса для цены мин, %"
            tooltip="Цена мин = прайс − скидка%. Поле «Цена мин» пересчитается автоматически."
          >
            <InputNumber
              min={0}
              max={100}
              style={{ width: "100%" }}
              addonAfter="%"
              placeholder="Например, 10"
            />
          </Form.Item>

          <Form.Item
            name="lowTradePrice"
            label="Цена мин (в выбранной валюте)"
            rules={[{ required: false }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} placeholder="Введите минимальную цену" />
          </Form.Item>

          <Form.Item
            name="upMarkupPercent"
            label="Наценка от прайса для цены до, %"
            tooltip="Цена до = прайс + наценка%. Поле «Цена до» пересчитается автоматически."
          >
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              addonAfter="%"
              placeholder="Например, 15"
            />
          </Form.Item>

          <Form.Item name="upTradePrice" label="Цена до (в выбранной валюте)">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Изменить характеристики"
        open={isCharacteristicsModalOpen}
        onCancel={() => {
          setIsCharacteristicsModalOpen(false);
          setSelectedItem(null);
        }}
        onOk={() => characteristicsForm.submit()}
        confirmLoading={characteristicsLoading}
        width={600}
      >
        <Form
          form={characteristicsForm}
          onFinish={handleSubmitCharacteristics}
          layout="vertical"
        >
          <Form.List name="characteristics">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, "key"]}
                      rules={[{ required: true, message: "Введите ключ" }]}
                      style={{ flex: 1 }}
                    >
                      <Input placeholder="Ключ характеристики" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "value"]}
                      rules={[{ required: true, message: "Введите значение" }]}
                      style={{ flex: 1 }}
                    >
                      <Input placeholder="Значение характеристики" />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button type="link" danger onClick={() => remove(name)}>
                        Удалить
                      </Button>
                    )}
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block>
                    + Добавить характеристику
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      <Modal
        title="Изменить категорию позиции"
        open={isAssignModalOpen}
        onCancel={() => {
          setIsAssignModalOpen(false);
          setSelectedItem(null);
          assignForm.resetFields();
        }}
        onOk={() => assignForm.submit()}
        confirmLoading={assignLoading}
        okText="Сохранить"
      >
        <Typography.Paragraph type="secondary">
          Позиция: <b>{selectedItem?.name}</b>
        </Typography.Paragraph>
        <Form form={assignForm} onFinish={handleAssignCategory} layout="vertical">
          <Form.Item
            name="categoryId"
            label="Категория"
            rules={[{ required: true, message: "Выберите категорию" }]}
          >
            <Select
              placeholder="Выберите категорию"
              options={categories.map((c) => ({ label: c.name, value: c._id }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Категории — ${type === "produces" ? "продукция" : "материалы"}`}
        open={isCategoriesModalOpen}
        onCancel={() => {
          setIsCategoriesModalOpen(false);
          setNewCategoryName("");
        }}
        footer={null}
        width={520}
      >
        <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
          <Input
            placeholder="Название новой категории"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onPressEnter={handleCreateCategory}
          />
          <Button type="primary" loading={categoryActionLoading} onClick={handleCreateCategory}>
            Добавить
          </Button>
        </Space.Compact>

        {categories.length === 0 ? (
          <Typography.Text type="secondary">Категорий пока нет</Typography.Text>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {categories.map((category) => (
              <div
                key={category._id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <span>
                  {category.name}
                  {type === "produces" && category.minProfitPercent != null && (
                    <Tag color="green" style={{ marginLeft: 8 }}>
                      мин {category.minProfitPercent}%
                    </Tag>
                  )}
                  {type === "produces" && category.maxDiscountPercent != null && (
                    <Tag color="purple" style={{ marginLeft: 4 }}>
                      скидка до {category.maxDiscountPercent}%
                    </Tag>
                  )}
                </span>
                <Space>
                  {type === "produces" && (
                    <Button type="link" onClick={() => handleOpenProfit(category)}>
                      Проценты
                    </Button>
                  )}
                  <Button type="link" onClick={() => handleRenameCategory(category)}>
                    Переименовать
                  </Button>
                  <Button type="link" danger onClick={() => handleDeleteCategory(category)}>
                    Удалить
                  </Button>
                </Space>
              </div>
            ))}
          </Space>
        )}
      </Modal>

      <Modal
        title={`Проценты умного прайса — ${profitCategory?.name ?? ""}`}
        open={!!profitCategory}
        onCancel={() => setProfitCategory(null)}
        onOk={handleSaveProfit}
        confirmLoading={profitLoading}
        okText="Сохранить"
        cancelText="Отмена"
        width={480}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Мин. цена = себестоимость × (1 + мин%) — пол продажи: ниже неё заказ СРМ и
          КП не пройдут. Изменение мин. % создаст запросы на смену цен.
          Макс. скидка — потолок уступки от прайса; цена после скидки в любом случае
          не опустится ниже мин. цены. Пусто — берётся дефолт из настроек.
        </Typography.Paragraph>
        <Form form={profitForm} layout="vertical">
          <Form.Item
            name="minProfitPercent"
            label="Мин. % прибыли (себестоимость → мин. цена)"
            rules={[{ type: "number", min: 0, message: "Не может быть отрицательным" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              addonAfter="%"
              placeholder="напр. 20"
            />
          </Form.Item>
          <Form.Item
            name="maxDiscountPercent"
            label="Макс. скидка от прайса, %"
            tooltip="Потолок уступки продажника. Цена после скидки всё равно не может быть ниже мин. цены."
            rules={[{ type: "number", min: 0, max: 100, message: "Допустимо от 0 до 100%" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={100}
              addonAfter="%"
              placeholder="напр. 15"
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const min = getFieldValue("minProfitPercent");
              if (min == null) return null;
              // Наглядный пример на себестоимости 10 — та же формула, что на бэкенде.
              return (
                <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  Пример: себестоимость 10 → мин. цена {(10 * (1 + min / 100)).toFixed(2)}
                </Typography.Paragraph>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Ручная себестоимость — ${manualCostProduce?.name ?? ""}`}
        open={!!manualCostProduce}
        onCancel={() => setManualCostProduce(null)}
        footer={[
          <Button
            key="clear"
            danger
            loading={manualCostLoading}
            disabled={!manualCostProduce?.manualCost}
            onClick={() => saveManualCost(null)}
          >
            Сбросить в авто
          </Button>,
          <Button key="cancel" onClick={() => setManualCostProduce(null)}>
            Отмена
          </Button>,
          <Button key="ok" type="primary" loading={manualCostLoading} onClick={handleSaveManualCost}>
            Сохранить
          </Button>,
        ]}
        width={460}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Задаёт нормативную себестоимость единицы. Если указана — <b>заменяет</b> расчёт по
          рецептуре везде: и как своя себестоимость, и как стоимость этого полуфабриката в
          других продуктах. USD считается от курса автоматически. Пусто/сброс — считать по рецептуре.
        </Typography.Paragraph>
        {manualCostProduce && (
          <Typography.Paragraph style={{ marginBottom: 12 }}>
            Сейчас по расчёту:{" "}
            <b>
              {manualCostProduce.recipeCost
                ? `${roundToThreeDecimals(manualCostProduce.recipeCost.UZS)} сум`
                : "—"}
            </b>
            {manualCostProduce.manualCost ? "  (сейчас стоит ручная)" : ""}
          </Typography.Paragraph>
        )}
        <Form form={manualCostForm} layout="vertical">
          <Form.Item
            name="amountUZS"
            label="Ручная себестоимость, сум"
            rules={[{ type: "number", min: 0, message: "Не может быть отрицательным" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} addonAfter="сум" placeholder="напр. 30" />
          </Form.Item>
        </Form>
      </Modal>

      <CostOverviewModal
        produceId={costOverviewProduce?._id || null}
        produceName={costOverviewProduce?.name}
        open={isCostOverviewOpen}
        onClose={() => {
          setIsCostOverviewOpen(false);
          setCostOverviewProduce(null);
        }}
      />
    </div>
  );
}
